<script setup>
import { ref, computed } from 'vue'
import { useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import SignaturePad from '@/components/SignaturePad.vue'
import { useToast } from '@/composables/useToast'

const props = defineProps({
  sign_type:       { type: String, required: true },     // 'charge' ou 'client'
  bon:             { type: Object, required: true },
  client:          { type: Object, default: () => ({}) },
  equipements:     { type: Array,  default: () => [] },
  fonctions:       { type: Array,  default: () => [] },
  items:           { type: Array,  default: () => [] },
  charge_nom:      { type: String, default: '' },
  show_dates:      { type: Boolean, default: false },
  can_see_costs:   { type: Boolean, default: false },
  submit_url:      { type: String, required: true },
  back_url:        { type: String, default: '/bons' },
})

const padRef = ref(null)
const { error: toastError } = useToast()

const isClient = computed(() => props.sign_type === 'client')

const titreSection = computed(() =>
  isClient.value ? 'Signature du client' : 'Signature du chargé de projet'
)
const titrePage = computed(() =>
  `${titreSection.value} — Bon #${props.bon.numero_str}`
)

const form = useForm({
  sign_responsable: '',
  sign_client:      '',
  nom_client:       '',
  email_client:     '',
})

function fmt(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric' })
}

function fmtNum(n) {
  if (n == null || n === '') return '—'
  return Number(n).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function effacer() {
  padRef.value?.clear()
}

function signer() {
  if (!padRef.value || padRef.value.isEmpty()) {
    toastError('Signature manquante')
    return
  }
  const dataURL = padRef.value.toDataURL()
  if (isClient.value) {
    form.sign_client = dataURL
  } else {
    form.sign_responsable = dataURL
  }
  form.transform((data) => ({ bon: data })).patch(props.submit_url)
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="flex items-center gap-3">
        <a :href="back_url" class="text-outline hover:text-on-surface transition-colors">
          <span class="material-symbols-outlined text-xl">arrow_back</span>
        </a>
        <span class="text-outline">/</span>
        <span class="text-sm font-black uppercase tracking-widest text-on-surface">{{ titreSection }}</span>
        <span class="text-outline">/</span>
        <span class="font-mono text-sm font-bold text-primary">{{ bon.projet_code }}</span>
        <span class="text-sm text-outline">{{ bon.projet_nom }}</span>
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-5xl mx-auto space-y-6">

        <!-- ── En-tête ──────────────────────────────────────────────────── -->
        <div>
          <h1 class="text-2xl font-black text-on-surface tracking-tight uppercase">
            {{ titreSection }} — Bon # {{ bon.numero_str }}
          </h1>
        </div>

      <!-- ── Détails du bon ──────────────────────────────────────────── -->
      <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
        <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40">
          <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Détails du bon</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-[auto_1fr] divide-y md:divide-y-0 md:divide-x divide-outline-variant/10">

          <div class="p-6 flex flex-col items-center justify-start gap-3 md:min-w-[200px] md:max-w-[260px]">
            <img v-if="client.logo_form"
                 :src="client.logo_form_is_url ? client.logo_form : `/images/client/${client.logo_form}`"
                 alt="Logo"
                 class="max-h-20 max-w-[180px] object-contain" />
            <span v-else class="material-symbols-outlined text-5xl text-outline/30">corporate_fare</span>
            <div v-if="client.address" class="text-xs text-outline text-center leading-relaxed" v-html="client.address" />
            <div v-if="client.tel" class="text-xs text-outline text-center">
              Tél : {{ client.tel }}<span v-if="client.fax"> &nbsp;|&nbsp; Fax : {{ client.fax }}</span>
            </div>
          </div>

          <div class="p-6">
            <dl class="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-4">
              <div>
                <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Création</dt>
                <dd class="text-sm text-on-surface">{{ fmt(bon.date_creation) }}</dd>
              </div>
              <div v-if="bon.num_ts">
                <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Nº TS</dt>
                <dd class="text-sm font-mono text-on-surface">{{ bon.num_ts }}</dd>
              </div>
              <div v-if="bon.type_bon">
                <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Type</dt>
                <dd class="text-sm text-on-surface capitalize">{{ bon.type_bon }}</dd>
              </div>
              <div>
                <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Projet</dt>
                <dd class="text-sm text-on-surface">
                  <span class="font-mono font-bold text-primary">{{ bon.projet_code }}</span>
                  <span class="text-outline ml-1">{{ bon.projet_nom }}</span>
                </dd>
              </div>
              <div v-if="charge_nom">
                <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Chargé de projet</dt>
                <dd class="text-sm text-on-surface">{{ charge_nom }}</dd>
              </div>
              <div v-if="bon.createur_nom">
                <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Créateur</dt>
                <dd class="text-sm text-on-surface">{{ bon.createur_nom }}</dd>
              </div>
              <div v-if="bon.debut || bon.fin">
                <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Période</dt>
                <dd class="text-sm text-on-surface">{{ fmt(bon.debut) }} → {{ fmt(bon.fin) }}</dd>
              </div>
              <div v-if="bon.chainage">
                <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Chaînage</dt>
                <dd class="text-sm font-mono text-on-surface">{{ bon.chainage }}</dd>
              </div>
              <div v-if="bon.description" class="col-span-2 sm:col-span-3">
                <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Description</dt>
                <dd class="text-sm text-on-surface">{{ bon.description }}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <!-- ── Équipements ─────────────────────────────────────────────── -->
      <div v-if="equipements.length" class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
        <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40">
          <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Équipements</h2>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-surface-container-low/40">
              <tr>
                <th class="px-6 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Quantité</th>
                <th class="px-6 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Unité</th>
                <th class="px-6 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Équipement</th>
                <template v-if="show_dates">
                  <th class="px-6 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Début</th>
                  <th class="px-6 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Fin</th>
                </template>
                <template v-if="can_see_costs">
                  <th class="px-6 py-3 text-right text-[10px] font-black uppercase tracking-widest text-outline">Taux</th>
                  <th class="px-6 py-3 text-right text-[10px] font-black uppercase tracking-widest text-outline">Coût</th>
                </template>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr v-for="(be, i) in equipements" :key="`eq-${i}`">
                <td class="px-6 py-3 text-on-surface">{{ fmtNum(be.quantite) }}</td>
                <td class="px-6 py-3 text-on-surface">{{ be.unite }}</td>
                <td class="px-6 py-3 text-on-surface"><span class="font-mono text-outline">{{ be.code }}</span> — {{ be.nom }}</td>
                <template v-if="show_dates">
                  <td class="px-6 py-3 text-on-surface">{{ fmt(be.debut) }}</td>
                  <td class="px-6 py-3 text-on-surface">{{ fmt(be.fin) }}</td>
                </template>
                <template v-if="can_see_costs">
                  <td class="px-6 py-3 text-right text-on-surface">{{ fmtNum(be.taux) }}</td>
                  <td class="px-6 py-3 text-right text-on-surface">{{ fmtNum(be.cout) }}</td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Fonctions ───────────────────────────────────────────────── -->
      <div v-if="fonctions.length" class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
        <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40">
          <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Fonctions</h2>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-surface-container-low/40">
              <tr>
                <th class="px-6 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Quantité</th>
                <th class="px-6 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Unité</th>
                <th class="px-6 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Type de fonction</th>
                <template v-if="show_dates">
                  <th class="px-6 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Début</th>
                  <th class="px-6 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Fin</th>
                </template>
                <template v-if="can_see_costs">
                  <th class="px-6 py-3 text-right text-[10px] font-black uppercase tracking-widest text-outline">Taux</th>
                  <th class="px-6 py-3 text-right text-[10px] font-black uppercase tracking-widest text-outline">Coût</th>
                </template>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr v-for="(bf, i) in fonctions" :key="`fn-${i}`">
                <td class="px-6 py-3 text-on-surface">{{ fmtNum(bf.quantite) }}</td>
                <td class="px-6 py-3 text-on-surface">{{ bf.unite }}</td>
                <td class="px-6 py-3 text-on-surface"><span class="font-mono text-outline">{{ bf.code }}</span> — {{ bf.description }}</td>
                <template v-if="show_dates">
                  <td class="px-6 py-3 text-on-surface">{{ fmt(bf.debut) }}</td>
                  <td class="px-6 py-3 text-on-surface">{{ fmt(bf.fin) }}</td>
                </template>
                <template v-if="can_see_costs">
                  <td class="px-6 py-3 text-right text-on-surface">{{ fmtNum(bf.taux) }}</td>
                  <td class="px-6 py-3 text-right text-on-surface">{{ fmtNum(bf.cout) }}</td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Items ───────────────────────────────────────────────────── -->
      <div v-if="items.length" class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
        <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40">
          <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Items</h2>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-surface-container-low/40">
              <tr>
                <th class="px-6 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Quantité</th>
                <th class="px-6 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Unité</th>
                <th class="px-6 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Description</th>
                <template v-if="can_see_costs">
                  <th class="px-6 py-3 text-right text-[10px] font-black uppercase tracking-widest text-outline">Taux</th>
                  <th class="px-6 py-3 text-right text-[10px] font-black uppercase tracking-widest text-outline">Coût</th>
                </template>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr v-for="(bi, i) in items" :key="`it-${i}`">
                <td class="px-6 py-3 text-on-surface">{{ fmtNum(bi.quantite) }}</td>
                <td class="px-6 py-3 text-on-surface">{{ bi.unite }}</td>
                <td class="px-6 py-3 text-on-surface">{{ bi.description }}</td>
                <template v-if="can_see_costs">
                  <td class="px-6 py-3 text-right text-on-surface">{{ fmtNum(bi.taux) }}</td>
                  <td class="px-6 py-3 text-right text-on-surface">{{ fmtNum(bi.cout) }}</td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Signature ───────────────────────────────────────────────── -->
      <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
        <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40">
          <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">{{ titreSection }}</h2>
        </div>
        <div class="p-6 space-y-5">

          <h3 class="text-center text-base font-bold text-on-surface">{{ charge_nom }}</h3>

          <div v-if="isClient" class="max-w-xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label for="nom_client" class="block text-[10px] font-black uppercase tracking-widest text-outline mb-1">Nom</label>
              <input
                id="nom_client"
                v-model="form.nom_client"
                type="text"
                class="w-full bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
            </div>
            <div>
              <label for="email_client" class="block text-[10px] font-black uppercase tracking-widest text-outline mb-1">Courriel</label>
              <input
                id="email_client"
                v-model="form.email_client"
                type="email"
                class="w-full bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
            </div>
          </div>

          <SignaturePad ref="padRef" />

          <div class="flex justify-center gap-3 pt-2">
            <button type="button" @click="effacer"
                    class="px-6 py-3 rounded-md text-xs font-black uppercase tracking-widest bg-surface-container-low border border-outline-variant/20 text-outline hover:text-on-surface hover:bg-surface-container-highest transition-all">
              Effacer
            </button>
            <button type="button" @click="signer" :disabled="form.processing"
                    class="px-8 py-3 rounded-md text-xs font-black uppercase tracking-widest bg-primary text-on-primary hover:brightness-110 active:scale-95 transition-all shadow-lg disabled:opacity-60 flex items-center gap-2">
              <span v-if="form.processing" class="material-symbols-outlined text-sm animate-spin">progress_activity</span>
              <span v-else class="material-symbols-outlined text-sm">draw</span>
              Signer
            </button>
          </div>

        </div>
      </div>

      </div>
    </div>
  </AppLayout>
</template>
